<template>
  <div class="container mx-auto px-4 py-2">
    <div class="flex items-center justify-between mb-4">
      <h1 class="text-xl font-semibold">Сбор данных</h1>
      <button v-can-write @click="openCreate" class="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700">
        Создать список материалов
      </button>
    </div>

    <!-- Поиск -->
    <div class="mb-3">
      <input
        v-model="search"
        type="text"
        class="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Поиск по названию или создателю..."
      />
    </div>

    <!-- Реестр списков -->
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-3 py-2 text-left border-b">Название</th>
            <th class="px-3 py-2 text-left border-b">Дата</th>
            <th class="px-3 py-2 text-left border-b">Создатель</th>
            <th class="px-3 py-2 text-left border-b w-32">Позиций</th>
            <th class="px-3 py-2 text-right border-b w-24">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="5" class="px-3 py-4 text-center text-gray-500">Загрузка...</td>
          </tr>
          <tr v-else-if="filteredRows.length === 0">
            <td colspan="5" class="px-3 py-4 text-center text-gray-500">{{ search ? 'Ничего не найдено' : 'Списков нет' }}</td>
          </tr>
          <tr
            v-for="row in pagedRows"
            :key="row.id"
            class="border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
            @click="openList(row.id)"
          >
            <td class="px-3 py-2">{{ row.name }}</td>
            <td class="px-3 py-2">{{ row.created_at }}</td>
            <td class="px-3 py-2">{{ row.creator || '—' }}</td>
            <td class="px-3 py-2">{{ row.items_count }}</td>
            <td class="px-3 py-2 text-right">
              <button v-can-write @click.stop="deleteList(row)" class="px-2 py-1 text-red-600 hover:underline">Удалить</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Пагинация -->
    <div v-if="totalPages > 1" class="flex items-center justify-end gap-2 mt-3 text-sm">
      <button @click="page--" :disabled="page <= 1" class="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed">Назад</button>
      <span class="text-gray-600">{{ page }} / {{ totalPages }}</span>
      <button @click="page++" :disabled="page >= totalPages" class="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed">Вперёд</button>
    </div>

    <!-- Модалка создания списка -->
    <div v-if="createVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">Создать список материалов</h2>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Название</label>
          <input
            v-model="newName"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Например: Сбор от 16.06"
            @keyup.enter="submitCreate"
          />
        </div>
        <div class="flex justify-end gap-2">
          <button @click="closeCreate" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300">
            Отмена
          </button>
          <button
            @click="submitCreate"
            :disabled="!newName.trim() || creating"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {{ creating ? 'Создание...' : 'Создать' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Модалка уведомления об ошибке -->
    <div v-if="errorVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <p class="text-sm text-gray-800 mb-4">{{ errorMessage }}</p>
        <div class="flex justify-end">
          <button @click="errorVisible = false" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700">
            Понятно
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DataCollectionIndex',
  data() {
    return {
      rows: [],
      loading: false,
      createVisible: false,
      creating: false,
      newName: '',
      search: '',
      page: 1,
      perPage: 20,
      errorVisible: false,
      errorMessage: '',
    };
  },
  computed: {
    filteredRows() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.rows;
      return this.rows.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.creator || '').toLowerCase().includes(q)
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.perPage));
    },
    pagedRows() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredRows.slice(start, start + this.perPage);
    },
  },
  watch: {
    search() {
      this.page = 1;
    },
    totalPages(n) {
      if (this.page > n) this.page = n;
    },
  },
  mounted() {
    this.loadList();
  },
  methods: {
    async loadList() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/data-collection');
        if (data?.success) {
          this.rows = data.data || [];
        }
      } catch (e) {
        this.notifyError(e, 'Не удалось загрузить списки');
      } finally {
        this.loading = false;
      }
    },
    openList(id) {
      this.$router.push({ name: 'DataCollectionShow', params: { id } });
    },
    openCreate() {
      this.newName = '';
      this.createVisible = true;
    },
    closeCreate() {
      this.createVisible = false;
    },
    async submitCreate() {
      if (!this.newName.trim()) return;
      this.creating = true;
      try {
        const { data } = await axios.post('/api/data-collection', { name: this.newName.trim() });
        if (data?.success) {
          this.closeCreate();
          this.$router.push({ name: 'DataCollectionShow', params: { id: data.data.id } });
        } else {
          this.notifyError(null, data?.message || 'Не удалось создать список');
        }
      } catch (e) {
        this.notifyError(e, 'Не удалось создать список');
      } finally {
        this.creating = false;
      }
    },
    async deleteList(row) {
      if (!confirm(`Удалить список «${row.name}»?`)) return;
      try {
        await axios.delete(`/api/data-collection/${row.id}`);
        await this.loadList();
      } catch (e) {
        this.notifyError(e, 'Не удалось удалить список');
      }
    },
    notifyError(error, fallback) {
      this.errorMessage = error?.response?.data?.message || fallback || 'Произошла ошибка, попробуйте ещё раз';
      if (error) {
        console.error(error);
      }
      this.errorVisible = true;
    },
  },
};
</script>
